import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  mdiArrowDown,
  mdiArrowLeft,
  mdiArrowUp,
  mdiCheckboxBlankCircleOutline,
  mdiCheckCircle,
  mdiClose,
  mdiMagnifyMinus,
  mdiMagnifyPlus,
  mdiMagnifyRemoveOutline,
  mdiMapMarker,
  mdiUndo,
} from '@mdi/js';
import Icon from '@mdi/react';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Button from 'components/Button/Button';
import { useTranslation } from 'react-i18next';
import {
  selectFloorplanPlacementsArray,
  selectProjectFloorplansArray,
} from 'store/floorplans/selectors/floorplans.selectors';
import {
  createFloorplanPlacement,
  updateFloorplanPlacement,
  deleteFloorplanPlacement,
} from 'store/floorplanPlacements';
import { getFloorplanDownloadUrl } from 'store/floorplans/services/getFloorplanDownloadUrl.service';
import { getThumbnailDownloadSignedUrl } from 'store/files/services/getThumbnailDownloadSignedUrl.service';
import { FloorplanDirectionCone } from 'components/FloorplanDirectionCone/FloorplanDirectionCone';
import { mergePlacementIntent } from './utils/mergePlacementIntent';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    border: theme.component.border,
  },
  dialogPaperFullScreen: {
    border: 'none',
    borderRadius: 0,
  },
  dialogContent: {
    paddingTop: 16,
    minHeight: 300,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      padding: '8px 12px',
      minHeight: 0,
    },
  },
  floorplanCard: {
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    border: `2px solid transparent`,
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  floorplanCardSelected: {
    borderColor: theme.palette.primary.main,
  },
  floorplanThumb: {
    width: '100%',
    height: 90,
    objectFit: 'cover',
    display: 'block',
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: 4,
  },
  floorplanThumbImgWrapper: {
    position: 'relative',
  },
  floorplanThumbPlacedBadge: {
    position: 'absolute',
    bottom: 6,
    right: 4,
    lineHeight: 0,
    background: 'rgba(0,0,0,0.95)',
    borderRadius: '50%',
    padding: 1,
  },
  floorplanName: {
    fontSize: '0.8rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  mapContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: 480,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      flex: 1,
      minHeight: 0,
      borderRadius: 0,
      border: 'none',
    },
  },
  mapImageWrapper: {
    // In-flow flex child — must shrink so image never exceeds container width.
    maxWidth: '100%',
    cursor: 'crosshair',
    userSelect: 'none',
    transformOrigin: '0 0',
    touchAction: 'none',
    willChange: 'transform',
  },
  mapImage: {
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: 480,
    [theme.breakpoints.down('sm')]: {
      maxHeight: '100%',
    },
  },
  pin: {
    position: 'absolute',
    transform: 'translate(-50%, -100%)',
    pointerEvents: 'none',
    zIndex: 5,
  },
  existingPin: {
    position: 'absolute',
    transform: 'translate(-50%, -100%)',
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  existingPinDelete: {
    background: 'rgba(0,0,0,0.5)',
    padding: 1,
    pointerEvents: 'auto',
    '&:hover': {
      background: 'rgba(0,0,0,0.75)',
    },
  },
  hint: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  otherImagePin: {
    position: 'absolute',
    transform: 'translate(-50%, -100%)',
    zIndex: 4,
    pointerEvents: 'none',
    opacity: 0.75,
  },
  // image strip (multi-image mode)
  imageStrip: {
    display: 'flex',
    gap: theme.spacing(0.75),
    overflowX: 'auto',
    padding: `${theme.spacing(0.5)}px 2px ${theme.spacing(1)}px`,
    marginBottom: theme.spacing(1),
    '&::-webkit-scrollbar': { height: 4 },
  },
  imageChipWrapper: {
    position: 'relative',
    flexShrink: 0,
    userSelect: 'none',
  },
  imageChipFrame: {
    width: 78,
    height: 78,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'border-color 0.15s, opacity 0.15s',
  },
  imageChipActive: {
    borderColor: theme.palette.primary.main,
  },
  imageChipExcluded: {
    opacity: 0.35,
  },
  imageChipImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    background: theme.palette.action.hover,
  },
  imageChipConfirmed: {
    position: 'absolute',
    bottom: 1,
    right: 1,
    pointerEvents: 'none',
    lineHeight: 0,
  },
  placementBannerSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    marginBottom: 8,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.success.main,
    color: '#fff',
  },
  placementBannerWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    marginBottom: 8,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.warning.main,
    color: '#fff',
  },
  zoomControls: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0,0,0,0.45)',
    borderRadius: theme.shape.borderRadius,
    padding: 2,
    gap: 2,
    '& .MuiIconButton-root': {
      color: 'white',
      padding: 4,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: 'rgba(255,255,255,0.3)',
    },
  },
  directionButtonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  directionButton: {
    fontSize: '0.75rem',
    padding: '2px 10px',
    minWidth: 0,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  directionButtonActive: {
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      background: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      color: '#fff',
    },
  },
}));

const MAX_ZOOM = 10;

const CLUSTER_THRESHOLD = 0.04;

// Screen-pixel radius around the pin that triggers move-pin mode
// instead of direction-set mode.
const DIRECTION_DRAG_THRESHOLD = 24;

function clusterPlacements(placements) {
  const clusters = [];
  for (const placement of placements) {
    let bestIdx = -1;
    let bestDistance = Infinity;
    for (let i = 0; i < clusters.length; i++) {
      const dx = placement.x - clusters[i].cx;
      const dy = placement.y - clusters[i].cy;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < CLUSTER_THRESHOLD && distance < bestDistance) {
        bestDistance = distance;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0) {
      const cluster = clusters[bestIdx];
      cluster.members.push(placement);
      cluster.cx =
        cluster.members.reduce((s, m) => s + m.x, 0) / cluster.members.length;
      cluster.cy =
        cluster.members.reduce((s, m) => s + m.y, 0) / cluster.members.length;
    } else {
      clusters.push({ members: [placement], cx: placement.x, cy: placement.y });
    }
  }
  return clusters.map((c) => c.members);
}

function FloorplanThumb({ floorplan, selected, onClick, hasPlacement }) {
  const classes = useStyles();
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (floorplan.id) {
      getFloorplanDownloadUrl(floorplan.project, floorplan.id)
        .then((url) => setSrc(url))
        .catch(() => setSrc(null));
    }
  }, [floorplan.id]);

  return (
    <Paper
      className={`${classes.floorplanCard} ${
        selected ? classes.floorplanCardSelected : ''
      }`}
      onClick={onClick}
      elevation={selected ? 3 : 1}
    >
      <div className={classes.floorplanThumbImgWrapper}>
        {src ? (
          <img
            className={classes.floorplanThumb}
            src={src}
            alt={floorplan.name}
          />
        ) : (
          <div className={classes.floorplanThumb} />
        )}
        {hasPlacement && (
          <div className={classes.floorplanThumbPlacedBadge}>
            <Icon path={mdiCheckCircle} size={0.75} color="#4caf50" />
          </div>
        )}
      </div>
      <p className={classes.floorplanName}>{floorplan.name}</p>
    </Paper>
  );
}

function ImageChip({
  filePermissionId,
  included,
  confirmed,
  onToggle,
  previewSrc,
}) {
  const classes = useStyles();
  const [src, setSrc] = useState(previewSrc ?? null);
  useEffect(() => {
    if (previewSrc) {
      setSrc(previewSrc);
      return;
    }
    getThumbnailDownloadSignedUrl(filePermissionId)
      .then((url) => {
        if (url) setSrc(url);
      })
      .catch(() => {});
  }, [filePermissionId, previewSrc]);

  return (
    <div className={classes.imageChipWrapper}>
      <div
        className={`${classes.imageChipFrame}${!included ? ` ${classes.imageChipExcluded}` : ''}`}
        onClick={onToggle}
      >
        {src ? (
          <img
            className={classes.imageChipImg}
            src={src}
            alt=""
            draggable={false}
          />
        ) : (
          <div className={classes.imageChipImg} />
        )}
      </div>
      {confirmed && (
        <div className={classes.imageChipConfirmed}>
          <Icon path={mdiCheckCircle} size={0.65} color="#4caf50" />
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: 3,
          right: 3,
          lineHeight: 0,
          background: 'rgba(0,0,0,0.5)',
          borderRadius: '50%',
          padding: 2,
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Icon
          path={included ? mdiCheckCircle : mdiCheckboxBlankCircleOutline}
          size={0.55}
          color="white"
        />
      </div>
    </div>
  );
}

function FloorplanPlacingStep({
  floorplan,
  filePermissionIds,
  projectId,
  onBack,
  confirmRef,
  flushDeletionsRef,
  deferred,
  previewUrls,
  onConfirmDeferred,
  // Sensor mode props
  mode = 'media',
  device = null,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isMulti = filePermissionIds.length > 1;

  const [src, setSrc] = useState(null);
  const [pendingPos, setPendingPos] = useState(null);
  const [pendingDirection, setPendingDirection] = useState(null);
  // 'position' = next interaction places the pin
  // 'direction' = pin is placed, next interaction sets direction
  const [placingPhase, setPlacingPhase] = useState('position');
  const [includedIds, setIncludedIds] = useState(
    () => new Set(filePermissionIds)
  );
  const [confirmedInSession, setConfirmedInSession] = useState(() => new Set());

  // Pending deletions — IDs of existing placements the user has marked for removal.
  // The actual DELETE call is deferred until the save button is pressed.
  const [pendingDeletions, setPendingDeletions] = useState(() => new Set());
  // Snapshot of confirmedInSession / includedIds taken just before the first
  // pending deletion, so we can fully restore on undo.
  const pendingDeletionBaselineRef = useRef(null);

  // Zoom / pan ————————————————————————————————————————————————————————————————
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // Refs mirror state so event handlers that run outside React (wheel) can
  // read the latest values without stale closures.
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  // Natural (un-zoomed) position of the image inside the container, set once
  // the image renders. Used as the pan origin so zooming keeps the image centred.
  const imgNaturalOffset = useRef({ x: 0, y: 0 });

  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const isDragging = useRef(false);

  // Pinch-to-zoom refs
  const isPinching = useRef(false);
  const lastTouchDist = useRef(0);
  const lastTouchMid = useRef({ x: 0, y: 0 });

  // Drag-to-pan refs (mouse only)
  const isDraggingPan = useRef(false);
  const panDragStart = useRef({ mouseX: 0, mouseY: 0, panX: 0, panY: 0 });

  // Single-finger touch drag-to-pan refs
  const isTouchPanning = useRef(false);
  const touchPanStart = useRef({ touchX: 0, touchY: 0, panX: 0, panY: 0 });
  // Set when a pinch gesture has occurred; prevents the final finger-lift
  // from being misread as a tap that places a pin.
  const justPinched = useRef(false);

  // Direction drag refs
  const isDraggingDirection = useRef(false);
  const isDraggingMovePin = useRef(false);
  const pinScreenAtDragStart = useRef({ x: 0, y: 0 });

  const pendingPosRef = useRef(null);
  const placingPhaseRef = useRef('position');
  const isDirectionLocked = useRef(false);
  const [directionLocked, setDirectionLocked] = useState(false);
  const lastTouchTime = useRef(0);
  const lastConfirmedSensorPos = useRef(null);
  const confirmedInSessionRef = useRef(new Set());

  const allPlacements = useSelector(selectFloorplanPlacementsArray);

  const { placementsByFile, allExistingPlacements } = useMemo(() => {
    const map = {};
    filePermissionIds.forEach((id) => {
      if (mode === 'sensors') {
        // In sensor mode, filter by deviceId
        map[id] = allPlacements.filter(
          (placement) =>
            placement.deviceId === id &&
            placement.floorplanId === floorplan.id &&
            !pendingDeletions.has(placement.id)
        );
      } else {
        // In media mode, filter by filePermissionId
        map[id] = allPlacements.filter(
          (placement) =>
            placement.filePermissionId === id &&
            placement.floorplanId === floorplan.id &&
            !pendingDeletions.has(placement.id)
        );
      }
    });
    const all = filePermissionIds.flatMap((id) => map[id] ?? []);
    return { placementsByFile: map, allExistingPlacements: all };
  }, [allPlacements, filePermissionIds, floorplan.id, pendingDeletions, mode]);

  useEffect(() => {
    if (floorplan.id) {
      getFloorplanDownloadUrl(floorplan.project, floorplan.id)
        .then((url) => setSrc(url))
        .catch(() => setSrc(null));
    }
  }, [floorplan.id]);

  // In sensor mode, preload existing placement as pending position
  useEffect(() => {
    if (mode === 'sensors' && allExistingPlacements.length > 0) {
      const existingPlacement = allExistingPlacements[0];
      setPendingPos({ x: existingPlacement.x, y: existingPlacement.y });
    }
  }, [mode, allExistingPlacements]);

  useEffect(() => {
    pendingPosRef.current = pendingPos;
  }, [pendingPos]);
  useEffect(() => {
    confirmedInSessionRef.current = confirmedInSession;
  }, [confirmedInSession]);
  useEffect(() => {
    placingPhaseRef.current = placingPhase;
  }, [placingPhase]);

  // Reset zoom/pan when switching floorplans
  useEffect(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    imgNaturalOffset.current = { x: 0, y: 0 };
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setPendingPos(null);
    setPendingDirection(null);
    setPlacingPhase('position');
    isDirectionLocked.current = false;
    setDirectionLocked(false);
    setPendingDeletions(new Set());
    pendingDeletionBaselineRef.current = null;
    // In deferred mode, all images need to be placeable again on each new floorplan
    if (deferred) {
      setIncludedIds(new Set(filePermissionIds));
      setConfirmedInSession(new Set());
    }
  }, [floorplan.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-confirm in media mode when pin is placed (sensor mode handled in drag/click handlers)
  useEffect(() => {
    if (mode !== 'sensors' && pendingPos && onConfirmDeferred) {
      // Avoid collecting intent until direction is locked (for proper direction capture)
      // or if direction is not applicable in this mode
      if (!directionLocked) {
        return;
      }

      // Avoid double-confirmation of the same position + direction
      if (
        lastConfirmedSensorPos.current &&
        lastConfirmedSensorPos.current.x === pendingPos.x &&
        lastConfirmedSensorPos.current.y === pendingPos.y &&
        lastConfirmedSensorPos.current.direction === pendingDirection
      ) {
        return;
      }

      const idsToPlace = [...includedIds].filter(
        (id) => !confirmedInSession.has(id)
      );

      if (idsToPlace.length === 0) {
        return;
      }

      const intent = {
        filePermissionIds: idsToPlace,
        floorplanId: floorplan.id,
        x: pendingPos.x,
        y: pendingPos.y,
        direction: pendingDirection,
      };

      onConfirmDeferred(intent);

      lastConfirmedSensorPos.current = {
        x: pendingPos.x,
        y: pendingPos.y,
        direction: pendingDirection,
      };
    }
  }, [
    pendingPos,
    mode,
    onConfirmDeferred,
    directionLocked,
    pendingDirection,
    includedIds,
    confirmedInSession,
  ]);

  // Clamp pan so image stays visible inside the container.
  // At zoom=1 always return {x:0,y:0} (flexbox centres the image).
  const getClampedPan = (px, py, z) => {
    if (z <= 1) return { x: 0, y: 0 };
    if (!containerRef.current || !imgRef.current) return { x: px, y: py };
    const cw = containerRef.current.offsetWidth;
    const ch = containerRef.current.offsetHeight;
    const iw = imgRef.current.offsetWidth;
    const ih = imgRef.current.offsetHeight;
    const ox = imgNaturalOffset.current.x;
    const oy = imgNaturalOffset.current.y;
    // The wrapper's layout position (flexbox) is (ox, oy) in the container.
    // transform: translate(px,py) scale(z) with transformOrigin '0 0' places a
    // local point (lx,ly) at (ox + lx*z + px, oy + ly*z + py) in the container.
    // So the image occupies [ox+px .. ox+iw*z+px] x [oy+py .. oy+ih*z+py].
    // When the zoomed image is smaller than the container, center it instead of clamping.
    const clampedX =
      iw * z <= cw
        ? cw / 2 - ox - (iw * z) / 2
        : Math.max(cw - ox - iw * z, Math.min(-ox, px));
    const clampedY =
      ih * z <= ch
        ? ch / 2 - oy - (ih * z) / 2
        : Math.max(ch - oy - ih * z, Math.min(-oy, py));
    return { x: clampedX, y: clampedY };
  };

  // Non-passive wheel listener — always zooms, always calls preventDefault.
  // No scroll mode needed: the image always fits fully at zoom=1.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      const prevZoom = zoomRef.current;
      const newZoom = Math.max(1, Math.min(MAX_ZOOM, prevZoom * factor));
      if (newZoom === prevZoom) return;
      if (newZoom <= 1) {
        zoomRef.current = 1;
        panRef.current = { x: 0, y: 0 };
        setZoom(1);
        setPan({ x: 0, y: 0 });
        return;
      }
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const { x: ox, y: oy } = imgNaturalOffset.current;
      const ratio = newZoom / prevZoom;
      // Keep the image point under the cursor fixed: (mx - ox - pan.x) / z is
      // constant, so nx = mx - ox - (mx - ox - pan.x) * ratio.
      const nx = mx - ox - (mx - ox - panRef.current.x) * ratio;
      const ny = my - oy - (my - oy - panRef.current.y) * ratio;
      const clamped = getClampedPan(nx, ny, newZoom);
      zoomRef.current = newZoom;
      panRef.current = clamped;
      setZoom(newZoom);
      setPan(clamped);
    };

    const handleHoverMove = (e) => {
      if (e.pointerType !== 'mouse') return;
      if (placingPhaseRef.current !== 'direction') return;
      if (isDirectionLocked.current) return;
      if (!pendingPosRef.current || !imgRef.current) return;
      if (Date.now() - lastTouchTime.current < 800) return;
      if (
        isDragging.current ||
        isDraggingDirection.current ||
        isDraggingMovePin.current
      )
        return;
      const imgRect = imgRef.current.getBoundingClientRect();
      const pinSx = imgRect.left + pendingPosRef.current.x * imgRect.width;
      const pinSy = imgRect.top + pendingPosRef.current.y * imgRect.height;
      const angle =
        ((Math.atan2(e.clientX - pinSx, -(e.clientY - pinSy)) * 180) / Math.PI +
          360) %
        360;
      setPendingDirection(Math.round(angle));
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('pointermove', handleHoverMove);
    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('pointermove', handleHoverMove);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getRelativePos = (e) => {
    if (!imgRef.current) return null;
    // getBoundingClientRect accounts for CSS transforms, so coordinate math
    // is always correct regardless of current zoom/pan.
    const rect = imgRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    };
  };

  // ─── Mouse handlers ─────────────────────────────────────────────────────────

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;

    if (Date.now() - lastTouchTime.current < 500) return;
    e.preventDefault();

    // ── Direction phase (or sensor mode position adjustment) ──────────────
    if (
      (mode === 'sensors' || placingPhase === 'direction') &&
      pendingPos &&
      imgRef.current
    ) {
      const imgRect = imgRef.current.getBoundingClientRect();
      const pinSx = imgRect.left + pendingPos.x * imgRect.width;
      const pinSy = imgRect.top + pendingPos.y * imgRect.height;
      const distToPin = Math.hypot(e.clientX - pinSx, e.clientY - pinSy);
      const computeAngle = (cx, cy) =>
        ((Math.atan2(cx - pinSx, -(cy - pinSy)) * 180) / Math.PI + 360) % 360;

      if (distToPin < DIRECTION_DRAG_THRESHOLD) {
        // Near pin: drag repositions pin
        isDraggingMovePin.current = true;
        isDragging.current = true;
        isDraggingPan.current = false;
        panDragStart.current = {
          mouseX: e.clientX,
          mouseY: e.clientY,
          panX: panRef.current.x,
          panY: panRef.current.y,
        };
        if (zoomRef.current > 1) {
          if (wrapperRef.current) wrapperRef.current.style.cursor = 'grabbing';
          document.body.style.cursor = 'grabbing';
        }
        const onMovePin = (moveEvent) => {
          const pos = getRelativePos(moveEvent);
          if (pos) setPendingPos(pos);
        };
        const onUpPin = () => {
          document.removeEventListener('mousemove', onMovePin);
          document.removeEventListener('mouseup', onUpPin);
          if (wrapperRef.current)
            wrapperRef.current.style.cursor = zoomRef.current > 1 ? 'grab' : '';
          document.body.style.cursor = '';
          isDraggingMovePin.current = false;
          isDragging.current = false;
          // In sensor mode, confirm the position after drag ends if it changed
          if (
            mode === 'sensors' &&
            pendingPosRef.current &&
            onConfirmDeferred
          ) {
            // Check if position actually changed
            const posChanged =
              !lastConfirmedSensorPos.current ||
              lastConfirmedSensorPos.current.x !== pendingPosRef.current.x ||
              lastConfirmedSensorPos.current.y !== pendingPosRef.current.y;

            if (posChanged) {
              const intent = {
                filePermissionIds: filePermissionIds,
                floorplanId: floorplan.id,
                x: pendingPosRef.current.x,
                y: pendingPosRef.current.y,
                direction: isDirectionLocked.current ? pendingDirection : null,
              };
              onConfirmDeferred(intent);
              lastConfirmedSensorPos.current = pendingPosRef.current;
            }
          }
        };
        document.addEventListener('mousemove', onMovePin);
        document.addEventListener('mouseup', onUpPin);
        return;
      }

      // Far from pin: In sensor mode, clicking repositions the pin
      if (mode === 'sensors') {
        const pos = getRelativePos(e);
        if (pos) {
          setPendingPos(pos);
          setPendingDirection(180);
          isDirectionLocked.current = false;
          setDirectionLocked(false);

          // Call API immediately on click if position changed
          if (onConfirmDeferred) {
            const posChanged =
              !lastConfirmedSensorPos.current ||
              lastConfirmedSensorPos.current.x !== pos.x ||
              lastConfirmedSensorPos.current.y !== pos.y;

            if (posChanged) {
              const intent = {
                filePermissionIds: filePermissionIds,
                floorplanId: floorplan.id,
                x: pos.x,
                y: pos.y,
                direction: null,
              };
              onConfirmDeferred(intent);
              lastConfirmedSensorPos.current = pos;
            }
          }
        }
        return;
      }

      if (zoomRef.current <= 1) {
        // zoom=1 (locked or not): drag/click always sets direction live.
        isDraggingDirection.current = true;
        pinScreenAtDragStart.current = { x: pinSx, y: pinSy };
        setPendingDirection(Math.round(computeAngle(e.clientX, e.clientY)));
        const onDirMove = (moveEvent) => {
          setPendingDirection(
            Math.round(computeAngle(moveEvent.clientX, moveEvent.clientY))
          );
        };
        const onDirUp = () => {
          document.removeEventListener('mousemove', onDirMove);
          document.removeEventListener('mouseup', onDirUp);
          isDraggingDirection.current = false;
          isDirectionLocked.current = true;
          setDirectionLocked(true);
        };
        document.addEventListener('mousemove', onDirMove);
        document.addEventListener('mouseup', onDirUp);
        return;
      }

      // zoom>1 (locked or not): drag pans, clean click sets+locks direction.
      isDragging.current = true;
      isDraggingPan.current = false;
      panDragStart.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        panX: panRef.current.x,
        panY: panRef.current.y,
      };
      if (wrapperRef.current) wrapperRef.current.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
      const onDirPanMove = (moveEvent) => {
        if (!isDragging.current) return;
        const dx = moveEvent.clientX - panDragStart.current.mouseX;
        const dy = moveEvent.clientY - panDragStart.current.mouseY;
        if (!isDraggingPan.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6))
          isDraggingPan.current = true;
        if (isDraggingPan.current) {
          const clamped = getClampedPan(
            panDragStart.current.panX + dx,
            panDragStart.current.panY + dy,
            zoomRef.current
          );
          panRef.current = clamped;
          setPan(clamped);
        }
      };
      const onDirPanUp = (upEvent) => {
        document.removeEventListener('mousemove', onDirPanMove);
        document.removeEventListener('mouseup', onDirPanUp);
        if (wrapperRef.current)
          wrapperRef.current.style.cursor = zoomRef.current > 1 ? 'grab' : '';
        document.body.style.cursor = '';
        const wasPanning = isDraggingPan.current;
        isDraggingPan.current = false;
        isDragging.current = false;
        if (!wasPanning) {
          setPendingDirection(
            Math.round(computeAngle(upEvent.clientX, upEvent.clientY))
          );
          isDirectionLocked.current = true;
          setDirectionLocked(true);
        }
      };
      document.addEventListener('mousemove', onDirPanMove);
      document.addEventListener('mouseup', onDirPanUp);
      return;
    }

    // ── Normal position / pan flow ────────────────────────────────────────────
    isDraggingDirection.current = false;
    isDragging.current = true;
    isDraggingPan.current = false;
    panDragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };

    if (zoomRef.current <= 1 && (mode === 'sensors' || includedIds.size > 0)) {
      const pos = getRelativePos(e);
      if (pos) {
        setPendingPos(pos);
        setPendingDirection(180);
        isDirectionLocked.current = false;
        setDirectionLocked(false);
        setPlacingPhase('direction');

        // In sensor mode, confirm the position immediately on initial click
        if (mode === 'sensors' && onConfirmDeferred) {
          const intent = {
            filePermissionIds: filePermissionIds,
            floorplanId: floorplan.id,
            x: pos.x,
            y: pos.y,
            direction: null,
          };
          onConfirmDeferred(intent);
          lastConfirmedSensorPos.current = pos;
        }
      }
    }
    if (zoomRef.current > 1) {
      if (wrapperRef.current) wrapperRef.current.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
    }

    const onMove = (moveEvent) => {
      if (!isDragging.current) return;
      const dx = moveEvent.clientX - panDragStart.current.mouseX;
      const dy = moveEvent.clientY - panDragStart.current.mouseY;
      if (zoomRef.current > 1) {
        if (!isDraggingPan.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
          isDraggingPan.current = true;
        }
        if (isDraggingPan.current) {
          const clamped = getClampedPan(
            panDragStart.current.panX + dx,
            panDragStart.current.panY + dy,
            zoomRef.current
          );
          panRef.current = clamped;
          setPan(clamped);
        }
        return;
      }
      if (includedIds.size === 0) return;
      const pos = getRelativePos(moveEvent);
      if (pos) {
        setPendingPos(pos);
        setPendingDirection(180);
        isDirectionLocked.current = false;
        setDirectionLocked(false);
        setPlacingPhase('direction');
      }
    };

    const onUp = (upEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (wrapperRef.current)
        wrapperRef.current.style.cursor = zoomRef.current > 1 ? 'grab' : '';
      document.body.style.cursor = '';
      const wasPanning = isDraggingPan.current;
      isDraggingPan.current = false;
      isDragging.current = false;
      // At zoom>1 a short click (no pan) places/repositions the pin.
      if (!wasPanning && zoomRef.current > 1 && includedIds.size > 0) {
        const pos = getRelativePos(upEvent);
        if (pos) {
          setPendingPos(pos);
          setPendingDirection(180);
          isDirectionLocked.current = false;
          setDirectionLocked(false);
          setPlacingPhase('direction');
        }
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ─── Touch handlers ──────────────────────────────────────────────────────────

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      isPinching.current = true;
      justPinched.current = true;
      isDragging.current = false;
      isTouchPanning.current = false;
      isDraggingDirection.current = false;
      isDraggingMovePin.current = false;
      // Only discard a pending pin if it hasn't been placed yet (position phase).
      // In direction phase the pin is already confirmed and must not be wiped.
      if (placingPhase === 'position') {
        setPendingPos(null);
      }
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      lastTouchDist.current = Math.hypot(
        t1.clientX - t0.clientX,
        t1.clientY - t0.clientY
      );
      lastTouchMid.current = {
        x: (t0.clientX + t1.clientX) / 2,
        y: (t0.clientY + t1.clientY) / 2,
      };
      e.preventDefault();
      return;
    }
    if (isPinching.current) return;
    if (justPinched.current) return;
    e.preventDefault();

    // ── Direction phase (or sensor mode position adjustment) ────────────────────
    if (
      (mode === 'sensors' || placingPhase === 'direction') &&
      pendingPos &&
      imgRef.current
    ) {
      const imgRect = imgRef.current.getBoundingClientRect();
      const pinSx = imgRect.left + pendingPos.x * imgRect.width;
      const pinSy = imgRect.top + pendingPos.y * imgRect.height;
      const distToPin = Math.hypot(
        e.touches[0].clientX - pinSx,
        e.touches[0].clientY - pinSy
      );
      // Always record touchPanStart so pan/direction logic has an origin.
      touchPanStart.current = {
        touchX: e.touches[0].clientX,
        touchY: e.touches[0].clientY,
        panX: panRef.current.x,
        panY: panRef.current.y,
      };
      if (distToPin < DIRECTION_DRAG_THRESHOLD) {
        // Near pin: drag always moves the pin
        isDraggingMovePin.current = true;
        isDraggingDirection.current = false;
        isDragging.current = true;
        isTouchPanning.current = false;
      } else {
        // Far from pin: tap or drag sets direction
        isDraggingDirection.current = true;
        isDraggingMovePin.current = false;
        isDragging.current = true;
        isTouchPanning.current = false;
        pinScreenAtDragStart.current = { x: pinSx, y: pinSy };
      }
      return;
    }

    // ── Normal position / pan flow ────────────────────────────────────────────
    isDraggingDirection.current = false;
    isDragging.current = true;
    isTouchPanning.current = false;
    touchPanStart.current = {
      touchX: e.touches[0].clientX,
      touchY: e.touches[0].clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    // Pin placement at zoom=1 is deferred to touchend (tap) or touchmove (drag)
    // so that the first finger of a pinch gesture does not accidentally place a pin.
  };

  const handleTouchMove = (e) => {
    if (isPinching.current || e.touches.length === 2) {
      e.preventDefault();
      isPinching.current = true;
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const newDist = Math.hypot(
        t1.clientX - t0.clientX,
        t1.clientY - t0.clientY
      );
      const newMid = {
        x: (t0.clientX + t1.clientX) / 2,
        y: (t0.clientY + t1.clientY) / 2,
      };
      if (lastTouchDist.current > 0 && containerRef.current) {
        const newZoom = Math.max(
          1,
          Math.min(
            MAX_ZOOM,
            zoomRef.current * (newDist / lastTouchDist.current)
          )
        );
        const rect = containerRef.current.getBoundingClientRect();
        const mid = { x: newMid.x - rect.left, y: newMid.y - rect.top };
        const lastMid = {
          x: lastTouchMid.current.x - rect.left,
          y: lastTouchMid.current.y - rect.top,
        };
        // Keep the world point under the pinch midpoint stable
        const { x: ox, y: oy } = imgNaturalOffset.current;
        const worldMidX = (lastMid.x - ox - panRef.current.x) / zoomRef.current;
        const worldMidY = (lastMid.y - oy - panRef.current.y) / zoomRef.current;
        const newPanX = mid.x - ox - worldMidX * newZoom;
        const newPanY = mid.y - oy - worldMidY * newZoom;
        const clamped =
          newZoom <= 1
            ? { x: 0, y: 0 }
            : getClampedPan(newPanX, newPanY, newZoom);
        zoomRef.current = newZoom;
        panRef.current = clamped;
        setZoom(newZoom);
        setPan(clamped);
      }
      lastTouchDist.current = newDist;
      lastTouchMid.current = newMid;
      return;
    }
    // ── Move-pin drag (must run before isDragging guard) ───────────────────
    if (isDraggingMovePin.current) {
      e.preventDefault();
      const pos = getRelativePos(e);
      if (pos) setPendingPos(pos);
      return;
    }

    // ── Direction drag (must run before isDragging guard) ─────────────────
    if (isDraggingDirection.current) {
      e.preventDefault();
      if (isDirectionLocked.current && zoomRef.current > 1) {
        // zoom>1, direction confirmed — large movement pans; small tap
        // re-sets direction on touchEnd.
        const totalDx = e.touches[0].clientX - touchPanStart.current.touchX;
        const totalDy = e.touches[0].clientY - touchPanStart.current.touchY;
        if (Math.abs(totalDx) > 6 || Math.abs(totalDy) > 6) {
          isDraggingDirection.current = false;
          isTouchPanning.current = true;
          const clamped = getClampedPan(
            touchPanStart.current.panX + totalDx,
            touchPanStart.current.panY + totalDy,
            zoomRef.current
          );
          panRef.current = clamped;
          setPan(clamped);
        }
        return;
      }
      // zoom=1 (locked or not) or zoom>1 not yet locked: live angle update.
      const dx = e.touches[0].clientX - pinScreenAtDragStart.current.x;
      const dy = e.touches[0].clientY - pinScreenAtDragStart.current.y;
      setPendingDirection(
        Math.round(((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360)
      );
      return;
    }

    if (!isDragging.current) return;
    if (zoomRef.current > 1) {
      const dx = e.touches[0].clientX - touchPanStart.current.touchX;
      const dy = e.touches[0].clientY - touchPanStart.current.touchY;
      if (!isTouchPanning.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        isTouchPanning.current = true;
      }
      if (isTouchPanning.current) {
        const clamped = getClampedPan(
          touchPanStart.current.panX + dx,
          touchPanStart.current.panY + dy,
          zoomRef.current
        );
        panRef.current = clamped;
        setPan(clamped);
      }
      return;
    }

    const pos = getRelativePos(e);
    if (pos) {
      setPendingPos(pos);
      setPendingDirection(180);
      isDirectionLocked.current = false;
      setDirectionLocked(false);
      setPlacingPhase('direction');

      // In sensor mode, confirm the position immediately on initial touch movement
      if (mode === 'sensors' && onConfirmDeferred) {
        const intent = {
          filePermissionIds: filePermissionIds,
          floorplanId: floorplan.id,
          x: pos.x,
          y: pos.y,
          direction: null,
        };
        onConfirmDeferred(intent);
        lastConfirmedSensorPos.current = pos;
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      isPinching.current = false;
    }
    if (e.touches.length === 0) {
      // All fingers lifted — safe to clear the pinch guard for the next gesture.
      justPinched.current = false;
    }

    // Move-pin mode: clean up and keep placingPhase as 'direction'.
    if (isDraggingMovePin.current) {
      isDraggingMovePin.current = false;
      isDragging.current = false;
      isTouchPanning.current = false;
      return;
    }

    // Direction mode: set direction from where finger lifted (handles taps too).
    if (isDraggingDirection.current) {
      isDraggingDirection.current = false;
      isDragging.current = false;
      // Capture panning flag before clearing it.
      const wasPanning = isTouchPanning.current;
      isTouchPanning.current = false;
      // Only set direction if the gesture was a tap (no pan occurred).
      if (!wasPanning && pendingPos && imgRef.current) {
        const touch = e.changedTouches[0];
        if (touch) {
          const imgRect = imgRef.current.getBoundingClientRect();
          const pinSx = imgRect.left + pendingPos.x * imgRect.width;
          const pinSy = imgRect.top + pendingPos.y * imgRect.height;
          const dx = touch.clientX - pinSx;
          const dy = touch.clientY - pinSy;
          setPendingDirection(
            Math.round(((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360)
          );
          isDirectionLocked.current = true;
          setDirectionLocked(true);
        }
      }
      return;
    }

    // At zoom=1 a clean tap (no pinch, no pan) places the pin.
    // Deferred from touchstart so the first finger of a pinch doesn't place it.
    if (
      isDragging.current &&
      !isTouchPanning.current &&
      !justPinched.current &&
      zoomRef.current <= 1 &&
      includedIds.size > 0
    ) {
      const syntheticE = { touches: e.changedTouches };
      const pos = getRelativePos(syntheticE);
      if (pos) {
        setPendingPos(pos);
        setPendingDirection(180);
        isDirectionLocked.current = false;
        setDirectionLocked(false);
        setPlacingPhase('direction');
      }
    }
    // At zoom>1 a short tap (no pan) places the pin, same as mouse click.
    // Skip if this touchend is part of a pinch gesture lifting off.
    if (
      isDragging.current &&
      !isTouchPanning.current &&
      !justPinched.current &&
      zoomRef.current > 1 &&
      includedIds.size > 0
    ) {
      const syntheticE = { touches: e.changedTouches };
      const pos = getRelativePos(syntheticE);
      if (pos) {
        setPendingPos(pos);
        setPendingDirection(180);
        isDirectionLocked.current = false;
        setDirectionLocked(false);
        setPlacingPhase('direction');
      }
    }
    isTouchPanning.current = false;
    isDragging.current = false;
    lastTouchTime.current = Date.now();
  };

  // ─── Zoom controls ───────────────────────────────────────────────────────────

  const applyZoom = (newZoom) => {
    if (newZoom <= 1) {
      zoomRef.current = 1;
      panRef.current = { x: 0, y: 0 };
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const clamped = getClampedPan(panRef.current.x, panRef.current.y, newZoom);
    zoomRef.current = newZoom;
    panRef.current = clamped;
    setZoom(newZoom);
    setPan(clamped);
  };

  // ─── Confirm / delete ────────────────────────────────────────────────────────

  const handleConfirm = () => {
    if (!pendingPos) return;
    if (!deferred && includedIds.size === 0) return;

    if (deferred) {
      // Deferred mode: collect placement intents without dispatching thunks
      const idsToPlace = [...includedIds].filter(
        (id) => !confirmedInSession.has(id)
      );
      if (idsToPlace.length === 0) return;
      if (onConfirmDeferred) {
        const intent = {
          filePermissionIds: idsToPlace,
          floorplanId: floorplan.id,
          x: pendingPos.x,
          y: pendingPos.y,
          direction: isDirectionLocked.current ? pendingDirection : null,
        };
        onConfirmDeferred(intent);
      }
      setConfirmedInSession(new Set([...confirmedInSession, ...idsToPlace]));
      setIncludedIds((prev) => {
        const next = new Set(prev);
        idsToPlace.forEach((id) => next.delete(id));
        return next;
      });
    } else if (isMulti) {
      // Batch: place all included, not-yet-confirmed images at this position
      const toBatch = filePermissionIds.filter(
        (id) => includedIds.has(id) && !confirmedInSession.has(id)
      );
      dispatch(
        createFloorplanPlacement({
          projectId,
          filePermissionIds: toBatch,
          floorplanId: floorplan.id,
          x: pendingPos.x,
          y: pendingPos.y,
          direction: isDirectionLocked.current ? pendingDirection : null,
        })
      );
      setConfirmedInSession(new Set([...confirmedInSession, ...toBatch]));
      setIncludedIds((prev) => {
        const next = new Set(prev);
        toBatch.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      const singleId = filePermissionIds[0];
      const existing = (placementsByFile[singleId] ?? [])[0];
      if (existing) {
        dispatch(
          updateFloorplanPlacement({
            projectId,
            placementId: existing.id,
            x: pendingPos.x,
            y: pendingPos.y,
            direction: isDirectionLocked.current ? pendingDirection : null,
          })
        );
      } else {
        dispatch(
          createFloorplanPlacement({
            projectId,
            filePermissionId: singleId,
            floorplanId: floorplan.id,
            x: pendingPos.x,
            y: pendingPos.y,
            direction: isDirectionLocked.current ? pendingDirection : null,
          })
        );
      }
      setConfirmedInSession(new Set([...confirmedInSession, singleId]));
    }

    setPendingPos(null);
    setPlacingPhase('position');
  };

  useEffect(() => {
    if (confirmRef) {
      confirmRef.current =
        pendingPos && (deferred || includedIds.size > 0) ? handleConfirm : null;
    }
    if (flushDeletionsRef) {
      flushDeletionsRef.current =
        pendingDeletions.size > 0
          ? () => {
              pendingDeletions.forEach((placementId) =>
                dispatch(deleteFloorplanPlacement({ projectId, placementId }))
              );
              setPendingDeletions(new Set());
              pendingDeletionBaselineRef.current = null;
            }
          : null;
    }
  }, [
    pendingPos,
    pendingDirection,
    deferred,
    includedIds,
    confirmedInSession,
    placementsByFile,
    floorplan,
    pendingDeletions,
    dispatch,
    projectId,
    filePermissionIds,
  ]);

  const handleDeleteCluster = (cluster) => {
    // Take a snapshot of restorable state before the first pending deletion.
    if (pendingDeletions.size === 0) {
      pendingDeletionBaselineRef.current = {
        confirmedInSession: new Set(confirmedInSession),
        includedIds: new Set(includedIds),
      };
    }
    // Queue the placement IDs for deletion (dispatched on save).
    setPendingDeletions((prev) => {
      const next = new Set(prev);
      cluster.forEach((p) => next.add(p.id));
      return next;
    });
    // Unmark confirmed and re-include affected filePermissionIds so the user
    // can immediately place a new pin if desired.
    const removedIds = cluster.map((p) => p.filePermissionId);
    setConfirmedInSession((prev) => {
      const next = new Set(prev);
      removedIds.forEach((id) => next.delete(id));
      return next;
    });
    setIncludedIds((prev) => new Set([...prev, ...removedIds]));
  };

  const handleUndoChanges = () => {
    // Restore confirmedInSession / includedIds from the pre-deletion snapshot.
    if (pendingDeletionBaselineRef.current) {
      setConfirmedInSession(
        new Set(pendingDeletionBaselineRef.current.confirmedInSession)
      );
      setIncludedIds(new Set(pendingDeletionBaselineRef.current.includedIds));
      pendingDeletionBaselineRef.current = null;
    }
    setPendingDeletions(new Set());
    // Also discard any unsaved pending pin placement.
    setPendingPos(null);
    setPendingDirection(null);
    setPlacingPhase('position');
  };

  // Picking a direction from the buttons is as explicit a choice as dragging the
  // cone on the image, so it locks the direction too. Without the lock the hover
  // preview overwrote the choice on the next mouse move, the "no direction"
  // button stayed lit alongside it, and save discarded it (see how the confirm
  // handlers below resolve the direction).
  const chooseDirection = (value) => {
    setPendingDirection(value);
    isDirectionLocked.current = true;
    setDirectionLocked(true);
  };

  const handleToggleId = (id) => {
    setIncludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Counter-scale pins so their visual size stays constant at any zoom level.
  // transformOrigin '50% 100%' keeps the pin tip anchored to the coordinate.
  const pinScale = 1 / zoom;
  const pinTransform = `translate(-50%, -100%) scale(${pinScale})`;
  const pinTransformOrigin = '50% 100%';

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        {onBack && (
          <Tooltip title={t('common.back')}>
            <IconButton size="small" onClick={onBack}>
              <Icon path={mdiArrowLeft} size={0.9} />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="subtitle2">{floorplan.name}</Typography>
      </div>

      {/* Image strip — only in multi-image mode (media mode only) */}
      {mode === 'media' && isMulti && (
        <div className={classes.imageStrip}>
          {filePermissionIds.map((id) => (
            <ImageChip
              key={id}
              filePermissionId={id}
              included={includedIds.has(id)}
              confirmed={confirmedInSession.has(id)}
              onToggle={() => handleToggleId(id)}
              previewSrc={previewUrls?.[id]}
            />
          ))}
        </div>
      )}

      {/* Placement status banner (media mode only) */}
      {mode === 'media' &&
        confirmedInSession.size > 0 &&
        (() => {
          const placed = confirmedInSession.size;
          const total = filePermissionIds.length;
          const allPlaced = placed >= total;
          const isSuccess = !isMulti || allPlaced;
          return (
            <div
              className={
                isSuccess
                  ? classes.placementBannerSuccess
                  : classes.placementBannerWarning
              }
            >
              {isSuccess && (
                <Icon path={mdiCheckCircle} size={0.8} color="#fff" />
              )}
              <Typography
                style={{ fontSize: '0.85rem', fontWeight: 500, color: '#fff' }}
              >
                {!isMulti
                  ? t('gallery.placement_status_one')
                  : allPlaced
                    ? t('gallery.placement_status_all', { count: total })
                    : t('gallery.placement_status_partial', { placed, total })}
              </Typography>
            </div>
          );
        })()}

      {!(
        isMulti &&
        includedIds.size === 0 &&
        confirmedInSession.size >= filePermissionIds.length
      ) && (
        <Typography className={classes.hint}>
          {mode === 'sensors'
            ? t('sensors.click_to_place_device', 'Click to place device')
            : placingPhase === 'direction' && pendingPos
              ? t('gallery.set_direction_hint')
              : !isMulti
                ? t('gallery.click_to_place_pin')
                : includedIds.size === 0
                  ? t('gallery.place_pin_none_selected')
                  : includedIds.size === 1
                    ? t('gallery.place_pin_one_selected')
                    : t('gallery.place_pin_multi_selected', {
                        count: includedIds.size,
                      })}
        </Typography>
      )}

      {/* Undo button — visible when there are pending changes (queued deletions or unsaved pin) */}
      {(pendingDeletions.size > 0 || pendingPos !== null) && (
        <div style={{ marginBottom: 8 }}>
          <button
            type="button"
            className={classes.directionButton}
            onClick={handleUndoChanges}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Icon
              path={mdiUndo}
              size={0.55}
              style={{ verticalAlign: 'middle' }}
            />
            {t('gallery.undo_changes')}
          </button>
        </div>
      )}

      {/* Direction buttons — visible once a pending pin is placed (media mode only) */}
      {mode === 'media' && pendingPos && includedIds.size > 0 && (
        <div className={classes.directionButtonGroup}>
          <Typography
            variant="caption"
            style={{ marginRight: 4, color: 'inherit' }}
          >
            {t('gallery.direction')}:
          </Typography>
          <button
            type="button"
            className={`${classes.directionButton}${!directionLocked || pendingDirection === null ? ` ${classes.directionButtonActive}` : ''}`}
            onClick={() => chooseDirection(null)}
          >
            {t('gallery.direction_none')}
          </button>
          <button
            type="button"
            className={`${classes.directionButton}${pendingDirection === 'up' ? ` ${classes.directionButtonActive}` : ''}`}
            onClick={() => chooseDirection('up')}
          >
            <Icon
              path={mdiArrowUp}
              size={0.55}
              style={{ verticalAlign: 'middle', marginRight: 2 }}
            />
            {t('gallery.direction_up')}
          </button>
          <button
            type="button"
            className={`${classes.directionButton}${pendingDirection === 'down' ? ` ${classes.directionButtonActive}` : ''}`}
            onClick={() => chooseDirection('down')}
          >
            <Icon
              path={mdiArrowDown}
              size={0.55}
              style={{ verticalAlign: 'middle', marginRight: 2 }}
            />
            {t('gallery.direction_down')}
          </button>
          {directionLocked && typeof pendingDirection === 'number' && (
            <Typography variant="caption" style={{ marginLeft: 4 }}>
              {t('gallery.direction_angle', { angle: pendingDirection })}
            </Typography>
          )}
        </div>
      )}

      <div ref={containerRef} className={classes.mapContainer}>
        {src ? (
          <>
            <div
              ref={wrapperRef}
              className={classes.mapImageWrapper}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                cursor: zoom > 1 ? 'grab' : undefined,
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                ref={imgRef}
                className={classes.mapImage}
                src={src}
                alt={floorplan.name}
                draggable={false}
                onLoad={() => {
                  if (!imgRef.current || !containerRef.current) return;
                  // Record the natural centred offset of the image inside the
                  // container. Used in getClampedPan so the pan limits account
                  // for the image not being at (0,0). Pan itself stays {x:0,y:0}
                  // at zoom=1 because flexbox already centres the image.
                  const el = containerRef.current;
                  const img = imgRef.current;
                  imgNaturalOffset.current = {
                    x: (el.offsetWidth - img.offsetWidth) / 2,
                    y: (el.offsetHeight - img.offsetHeight) / 2,
                  };
                }}
              />
              {/* All existing placements — clustered, all deletable (hidden in sensor mode) */}
              {mode !== 'sensors' &&
                clusterPlacements(allExistingPlacements).map((cluster) => {
                  const key = cluster
                    .map((p) => p.id)
                    .sort()
                    .join('-');
                  const cx =
                    cluster.reduce((s, p) => s + p.x, 0) / cluster.length;
                  const cy =
                    cluster.reduce((s, p) => s + p.y, 0) / cluster.length;

                  if (cluster.length === 1) {
                    const placement = cluster[0];
                    return (
                      <div
                        key={key}
                        className={classes.existingPin}
                        style={{
                          left: `${placement.x * 100}%`,
                          top: `${placement.y * 100}%`,
                          transform: pinTransform,
                          transformOrigin: pinTransformOrigin,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <FloorplanDirectionCone
                          direction={placement.direction}
                          zoom={zoom}
                        />
                        <Icon path={mdiMapMarker} size={1.2} color="#1565c0" />
                        <IconButton
                          className={classes.existingPinDelete}
                          size="small"
                          onClick={() => handleDeleteCluster(cluster)}
                        >
                          <Icon path={mdiClose} size={0.6} color="white" />
                        </IconButton>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={key}
                      style={{
                        position: 'absolute',
                        left: `${cx * 100}%`,
                        top: `${cy * 100}%`,
                        transform: pinTransform,
                        transformOrigin: pinTransformOrigin,
                        zIndex: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        style={{
                          background: '#1565c0',
                          color: 'white',
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                        onClick={() => handleDeleteCluster(cluster)}
                      >
                        {cluster.length}
                      </IconButton>
                    </div>
                  );
                })}
              {/* Pending position for active image */}
              {pendingPos && includedIds.size > 0 && (
                <div
                  className={classes.pin}
                  style={{
                    left: `${pendingPos.x * 100}%`,
                    top: `${pendingPos.y * 100}%`,
                    transform: pinTransform,
                    transformOrigin: pinTransformOrigin,
                  }}
                >
                  {mode === 'media' && (
                    <FloorplanDirectionCone
                      direction={pendingDirection}
                      zoom={zoom}
                      color={
                        directionLocked ? 'rgba(255, 214, 0, 0.55)' : undefined
                      }
                      strokeColor={
                        directionLocked ? 'rgba(200, 160, 0, 0.9)' : undefined
                      }
                    />
                  )}
                  <Icon path={mdiMapMarker} size={1.5} color="#d32f2f" />
                </div>
              )}
            </div>

            {/* Zoom controls overlay */}
            <div className={classes.zoomControls}>
              <Tooltip title={t('common.zoom_in')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={zoom >= MAX_ZOOM}
                    onClick={() =>
                      applyZoom(Math.min(MAX_ZOOM, zoomRef.current * 1.3))
                    }
                  >
                    <Icon path={mdiMagnifyPlus} size={0.8} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.zoom_out')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={zoom <= 1}
                    onClick={() =>
                      applyZoom(Math.max(1, zoomRef.current / 1.3))
                    }
                  >
                    <Icon path={mdiMagnifyMinus} size={0.8} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.zoom_reset')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={zoom <= 1}
                    onClick={() => {
                      zoomRef.current = 1;
                      panRef.current = { x: 0, y: 0 };
                      setZoom(1);
                      setPan({ x: 0, y: 0 });
                    }}
                  >
                    <Icon path={mdiMagnifyRemoveOutline} size={0.8} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </>
        ) : (
          <div style={{ padding: 40, color: '#888' }}>
            {t('common.loading')}...
          </div>
        )}
      </div>
    </>
  );
}

export function PlaceOnFloorplanDialog({
  open,
  onClose,
  filePermissionId,
  filePermissionIds: filePermissionIdsProp,
  projectId,
  initialFloorplanId,
  // Deferred mode props
  deferred,
  forcedFloorplan,
  previewUrls,
  mandatoryProgress,
  onDeferredComplete,
  // Sensor mode props
  mode = 'media', // 'media' | 'sensors'
  device = null, // For sensor mode
  onSensorPlaced = null, // For sensor mode: callback when device is placed
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const confirmRef = useRef(null);
  const flushDeletionsRef = useRef(null);
  // Accumulates deferred placement intents within one dialog session
  const deferredCollectedRef = useRef([]);
  const filePermissionIds = useMemo(
    () => filePermissionIdsProp ?? (filePermissionId ? [filePermissionId] : []),
    [filePermissionIdsProp, filePermissionId]
  );

  const floorplans = useSelector(selectProjectFloorplansArray);
  const allPlacements = useSelector(selectFloorplanPlacementsArray);
  const [selectedFloorplan, setSelectedFloorplan] = useState(null);

  const placedFloorplanIds = useMemo(() => {
    const ids = new Set();
    allPlacements.forEach((p) => {
      if (filePermissionIds.includes(p.filePermissionId)) {
        ids.add(p.floorplanId);
      }
    });
    return ids;
  }, [allPlacements, filePermissionIds]);

  // In deferred mode the forced floorplan drives the step; reset collection on open
  useEffect(() => {
    if (open) {
      if (deferred) {
        deferredCollectedRef.current = [];
      } else if (initialFloorplanId) {
        const initial = floorplans.find((f) => f.id === initialFloorplanId);
        if (initial) setSelectedFloorplan(initial);
      } else if (floorplans.length === 1) {
        setSelectedFloorplan(floorplans[0]);
      }
    }
    if (!open) {
      setSelectedFloorplan(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setSelectedFloorplan(null);
    onClose();
  };

  // Called by FloorplanPlacingStep for each confirmed pin in deferred mode.
  // The same pin arrives here repeatedly, so intents are merged rather than
  // appended — see mergePlacementIntent.
  const handleConfirmDeferred = (intent) => {
    deferredCollectedRef.current = mergePlacementIntent(
      deferredCollectedRef.current,
      intent
    );
  };

  const activeFloorplan =
    mode === 'sensors'
      ? forcedFloorplan
      : deferred
        ? forcedFloorplan
        : selectedFloorplan;

  const dialogTitle = (() => {
    if (mode === 'sensors') {
      return `${t('sensors.place_device', 'Place device')} ${device?.lixaniName || device?.name || ''}`;
    }
    if (mandatoryProgress && mandatoryProgress.total > 1) {
      return `${t('gallery.place_on_floorplan')} (${mandatoryProgress.current}/${mandatoryProgress.total})`;
    }
    return t('gallery.place_on_floorplan');
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{
        paper: `${classes.dialogPaper}${fullScreen ? ` ${classes.dialogPaperFullScreen}` : ''}`,
      }}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        onTouchStart: (e) => e.stopPropagation(),
        onTouchMove: (e) => e.stopPropagation(),
        onTouchEnd: (e) => e.stopPropagation(),
      }}
    >
      <DialogHeader title={dialogTitle} onClose={handleClose} />
      <DialogContent className={classes.dialogContent}>
        {activeFloorplan ? (
          <FloorplanPlacingStep
            floorplan={activeFloorplan}
            filePermissionIds={filePermissionIds}
            projectId={projectId}
            onBack={
              mode === 'sensors'
                ? undefined
                : deferred
                  ? undefined
                  : () => setSelectedFloorplan(null)
            }
            confirmRef={confirmRef}
            flushDeletionsRef={flushDeletionsRef}
            deferred={deferred || mode === 'sensors'}
            previewUrls={previewUrls}
            onConfirmDeferred={
              mode === 'sensors' ? onSensorPlaced : handleConfirmDeferred
            }
            mode={mode}
            device={device}
          />
        ) : floorplans.length === 0 ? (
          <Typography style={{ color: '#888', padding: 16 }}>
            {t('gallery.no_floorplans')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {floorplans.map((floorplan) => (
              <Grid item key={floorplan.id} xs={6} sm={4} md={3}>
                <FloorplanThumb
                  floorplan={floorplan}
                  selected={false}
                  onClick={() => setSelectedFloorplan(floorplan)}
                  hasPlacement={placedFloorplanIds.has(floorplan.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose}>{t('common.close')}</Button>
        {mode !== 'sensors' && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              // Flush any pending deletions and pending pin before completing
              if (flushDeletionsRef.current) {
                flushDeletionsRef.current();
              }
              if (confirmRef.current) {
                confirmRef.current();
              }
              if (deferred) {
                if (deferredCollectedRef.current.length === 0) {
                  // Nothing placed — treat as cancel
                  handleClose();
                } else if (onDeferredComplete) {
                  onDeferredComplete(deferredCollectedRef.current);
                  deferredCollectedRef.current = [];
                }
              } else {
                handleClose();
              }
            }}
          >
            {t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
